import { cn } from '@theprintbar/shared';
import React, { ButtonHTMLAttributes, forwardRef } from 'react';
import { Spinner } from '../Spinner';

export const defaultButtonClasses =
	'relative group inline-flex items-center justify-center rounded-full overflow-hidden focus:outline-none transition-all duration-500 whitespace-nowrap focus:outline-none';

export enum ButtonVariant {
	primary = 'bg-blue border border-blue hover:scale-[1.01] disabled:scale-100 text-white stroke-white disabled:bg-grey/20 disabled:text-grey disabled:border-grey',
	outline = 'bg-transparent border border-blue text-blue hover:bg-blue hover:text-white disabled:text-grey disabled:hover:bg-transparent disabled:hover:text-grey disabled:border-grey',
	transparent = 'bg-transparent border-none text-blue rounded-none transition-none duration-0',
	coloured = 'border border-blue',
	error = 'bg-red border border-red hover:scale-[1.01] disabled:scale-100 text-white stroke-white disabled:bg-grey/20 disabled:text-grey disabled:border-grey',
	warning = 'bg-orange border border-orange hover:scale-[1.01] disabled:scale-100 text-white stroke-white disabled:bg-grey/20 disabled:text-grey disabled:border-grey',
	outlineWarning = 'bg-transparent border border-orange text-orange hover:bg-orange hover:text-white disabled:text-grey disabled:hover:bg-transparent disabled:hover:text-grey disabled:border-grey',
}

export enum ButtonSize {
	XS = 'px-2.5 py-1 text-[11px]',
	S = 'px-3 py-[5px] text-xs',
	M = 'px-[14px] py-[7px] text-sm',
	L = 'px-[34px] py-[13px] text-lg',
}

export const getButtonIconSize = (size?: ButtonSize) => {
	switch (size) {
		case ButtonSize.XS:
			return 'h-2 w-2';
		case ButtonSize.S:
			return 'h-2.5 w-2.5';
		case ButtonSize.L:
			return 'h-4 w-4';
		default:
			return 'h-4 w-4';
	}
};

export const getButtonTextSize = (size?: ButtonSize) => {
	switch (size) {
		case ButtonSize.XS:
			return 'text-[11px]';
		case ButtonSize.S:
			return 'text-xs';
		case ButtonSize.L:
			return 'text-xl';
		default:
			return 'text-base';
	}
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	icon?: any;
	partner?: boolean;
	iconClass?: string;
	circle?: boolean;
	spinner?: boolean;
	variant?: ButtonVariant;
	size?: ButtonSize;
	iconSize?: ButtonSize;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			children,
			variant = ButtonVariant.primary,
			partner,
			size = ButtonSize.M,
			iconSize,
			spinner,
			icon: Icon,
			iconClass,
			circle,
			className,
			disabled,
			type,
			...props
		},
		ref,
	) => (
		<button
			className={cn(
				defaultButtonClasses,
				variant,
				circle ? 'p-2' : size,
				circle && size === ButtonSize.XS && 'p-1.5',
				disabled && 'text-grey',
				variant === ButtonVariant.transparent && '!shadow-none',
				variant === ButtonVariant.primary && partner && 'bg-orange border-blue text-blue stroke-blue',
				className,
			)}
			disabled={disabled || spinner}
			type={type || 'button'}
			ref={ref}
			{...props}
		>
			<span className={cn(getButtonTextSize(size), size === ButtonSize.L && 'mt-0.5')}>{children}</span>
			{spinner ? (
				<Spinner className={cn('text-grey', getButtonIconSize(iconSize || size), size !== ButtonSize.L ? 'ml-1.5' : 'ml-2')} />
			) : Icon ? (
				<Icon
					className={cn(!circle && (size !== ButtonSize.L ? 'ml-1.5' : 'ml-2'), getButtonIconSize(iconSize || size), iconClass)}
					aria-hidden="true"
				/>
			) : null}
		</button>
	),
);

export const PrimaryButton = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
	<Button {...props} variant={ButtonVariant.primary} ref={ref} />
));

export const OutlineButton = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
	<Button {...props} variant={ButtonVariant.outline} ref={ref} />
));

export const TransparentButton = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
	<Button {...props} variant={ButtonVariant.transparent} ref={ref} />
));

export const ColouredButton = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
	<Button {...props} variant={ButtonVariant.coloured} ref={ref} />
));

export const ErrorButton = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
	<Button {...props} variant={ButtonVariant.error} ref={ref} />
));

export const WarningButton = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
	<Button {...props} variant={ButtonVariant.warning} ref={ref} />
));
export const OutlineWarningButton = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
	<Button {...props} variant={ButtonVariant.outlineWarning} ref={ref} />
));
